/**
 * Scroll-triggered GSAP Animations
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Initialize scroll-triggered animations
 */
export function initScrollAnimations() {
  // Fade up animation for elements with .animate-fade-up class
  gsap.utils.toArray('.animate-fade-up').forEach((element) => {
    gsap.from(element, {
      y: 60,
      opacity: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: element,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  });

  // Staggered fade in for lists and grids
  gsap.utils.toArray('.animate-stagger').forEach((container) => {
    const children = container.children;
    if (children.length === 0) return;

    gsap.from(children, {
      y: 40,
      opacity: 0,
      duration: 0.6,
      stagger: 0.15,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: container,
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    });
  });

  // Scale in animation for images
  gsap.utils.toArray('.animate-scale-in').forEach((element) => {
    gsap.from(element, {
      scale: 0.9,
      opacity: 0,
      duration: 1,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: element,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  });

  // Parallax effect
  gsap.utils.toArray('.parallax').forEach((element) => {
    const depth = parseFloat(element.dataset.parallaxDepth) || 0.2;
    gsap.to(element, {
      y: () => -100 * depth,
      ease: 'none',
      scrollTrigger: {
        trigger: element,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      },
    });
  });

  // Reveal text animation (clip-path based)
  gsap.utils.toArray('.animate-reveal-text').forEach((element) => {
    gsap.from(element, {
      clipPath: 'inset(0 100% 0 0)',
      duration: 1,
      ease: 'power3.inOut',
      scrollTrigger: {
        trigger: element,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  });

  // Slide in from left
  gsap.utils.toArray('.animate-slide-left').forEach((element) => {
    gsap.from(element, {
      x: -100,
      opacity: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: element,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  });

  // Slide in from right
  gsap.utils.toArray('.animate-slide-right').forEach((element) => {
    gsap.from(element, {
      x: 100,
      opacity: 0,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: element,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  });

  // Blur in animation
  gsap.utils.toArray('.animate-blur-in').forEach((element) => {
    gsap.from(element, {
      opacity: 0,
      filter: 'blur(10px)',
      duration: 1,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: element,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });
  });
}

/**
 * Refresh ScrollTrigger - call after dynamic content loads
 */
export function refreshScrollTrigger() {
  ScrollTrigger.refresh();
}

/**
 * Kill all ScrollTrigger instances - useful for cleanup
 */
export function killScrollTriggers() {
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
}
